import type { ComponentType } from 'react'
import EnergyClassBadge from '../../../../components/EnergyClassBadge'
import type { CatalogProductFieldDefinition } from '../../../pluginFields'

export type ProductFieldRendererProps = {
  field: CatalogProductFieldDefinition
  value: unknown
  className?: string
}

export type ProductFieldRendererDefinition = {
  key: string
  label: string
  component: ComponentType<ProductFieldRendererProps>
  aliases?: string[]
  metadata?: Record<string, unknown>
}

const LEGACY_RENDERER_KEY_ALIASES = new Map([
  ['default', 'text'],
  ['boolean-toggle', 'boolean'],
  ['multiline', 'text'],
  ['energy-class-badge', 'energy-badge'],
  ['air-conditioner-energy-badge', 'energy-badge'],
  ['air-conditioner-wifi-badge', 'wifi-badge'],
  ['air-conditioner-refrigerant-badge', 'badge'],
])

export const normalizeRendererKey = (value: string): string => {
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')

  return LEGACY_RENDERER_KEY_ALIASES.get(normalized) || normalized
}

export const getProductFieldRendererKey = (
  field: Pick<CatalogProductFieldDefinition, 'rendererKey' | 'storefront'>,
): string | null => {
  const canonical = typeof field.rendererKey === 'string' ? normalizeRendererKey(field.rendererKey) : ''
  if (canonical) return canonical

  const legacy = typeof field.storefront.renderer === 'string' ? normalizeRendererKey(field.storefront.renderer) : ''
  return legacy || null
}

const renderBadgeValue = (value: unknown): string => {
  if (Array.isArray(value)) return value.map((entry) => String(entry)).join(', ')
  if (typeof value === 'boolean') return value ? 'Si' : 'No'
  if (value === null || value === undefined) return '-'
  const rendered = String(value).trim()
  return rendered || '-'
}

const EnergyBadgeRenderer = ({ field, value, className }: ProductFieldRendererProps) => {
  if (typeof value !== 'string') return <GenericBadgeRenderer field={field} value={value} className={className} />
  return <EnergyClassBadge value={value} className={className} />
}

const GenericBadgeRenderer = ({
  value,
  className = '',
}: ProductFieldRendererProps) => {
  return (
    <span className={`inline-flex items-center text-base font-black text-slate-950 ${className}`}>
      {renderBadgeValue(value)}
    </span>
  )
}

const WifiBadgeRenderer = (props: ProductFieldRendererProps) => <GenericBadgeRenderer {...props} />

const WarrantyBadgeRenderer = (props: ProductFieldRendererProps) => <GenericBadgeRenderer {...props} />

const BUILT_IN_RENDERERS: ProductFieldRendererDefinition[] = [
  {
    key: 'energy-badge',
    label: 'Energy badge',
    component: EnergyBadgeRenderer,
    aliases: ['energy-class-badge', 'air-conditioner-energy-badge'],
    metadata: { surface: 'storefront', kind: 'badge' },
  },
  {
    key: 'badge',
    label: 'Badge',
    component: GenericBadgeRenderer,
    aliases: ['air-conditioner-refrigerant-badge'],
    metadata: { surface: 'storefront', kind: 'badge' },
  },
  {
    key: 'wifi-badge',
    label: 'Wi-Fi badge',
    component: WifiBadgeRenderer,
    aliases: ['air-conditioner-wifi-badge'],
    metadata: { surface: 'storefront', kind: 'badge' },
  },
  {
    key: 'warranty-badge',
    label: 'Warranty badge',
    component: WarrantyBadgeRenderer,
    metadata: { surface: 'storefront', kind: 'badge' },
  },
]

const createRendererRegistry = (definitions: ProductFieldRendererDefinition[]) => {
  const byKey = new Map<string, ProductFieldRendererDefinition>()
  for (const definition of definitions) {
    addRendererDefinition(byKey, definition)
  }
  return byKey
}

const addRendererDefinition = (
  registry: Map<string, ProductFieldRendererDefinition>,
  definition: ProductFieldRendererDefinition,
) => {
  registry.set(normalizeRendererKey(definition.key), definition)
  for (const alias of definition.aliases || []) {
    registry.set(normalizeRendererKey(alias), definition)
  }
}

const PRODUCT_FIELD_RENDERER_REGISTRY = createRendererRegistry(BUILT_IN_RENDERERS)

export const registerProductFieldRenderer = (definition: ProductFieldRendererDefinition): void => {
  addRendererDefinition(PRODUCT_FIELD_RENDERER_REGISTRY, definition)
}

export const resolveRenderer = (rendererKey: string | null | undefined): ProductFieldRendererDefinition | null => {
  if (!rendererKey) return null
  const normalized = normalizeRendererKey(rendererKey)
  if (!normalized || normalized === 'text') return null
  return PRODUCT_FIELD_RENDERER_REGISTRY.get(normalized) ?? null
}

export const resolveRendererForField = (
  field: Pick<CatalogProductFieldDefinition, 'rendererKey' | 'storefront'>,
): ProductFieldRendererDefinition | null => resolveRenderer(getProductFieldRendererKey(field))

export const hasResolvedProductFieldRenderer = (
  field: Pick<CatalogProductFieldDefinition, 'rendererKey' | 'storefront'>,
): boolean => resolveRendererForField(field) !== null
